import React, { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from './supabaseClient';

import { 
  AlertCircle, Package, MapPin, Calendar, Users, DollarSign, 
  TrendingUp, Menu, X, Plus, Search, Edit, Trash2, 
  Home, Building2, ChevronDown, ChevronUp, User, Mail, 
  Phone, CheckCircle, Loader, Shield, FileText, BarChart3, 
  LogOut, Check, Bell, XCircle, Clock, Info
} from 'lucide-react';

// ============================================
// TOAST NOTIFICATION SYSTEM
// ============================================

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};

function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 3000);
  };

  const showSuccess = (message) => addToast(message, 'success');
  const showError = (message) => addToast(message, 'error');
  const showWarning = (message) => addToast(message, 'warning');
  const showInfo = (message) => addToast(message, 'info');

  return (
    <ToastContext.Provider value={{ showSuccess, showError, showWarning, showInfo }}>
      {children}
      <ToastContainer toasts={toasts} setToasts={setToasts} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, setToasts }) {
  const removeToast = (id) => setToasts(prev => prev.filter(toast => toast.id !== id));

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      maxWidth: '400px'
    }}>
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onClose={removeToast} />
      ))}
    </div>
  );
}

function Toast({ toast, onClose }) {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(toast.id), 300);
  };

  const config = {
    success: { icon: CheckCircle, bg: '#10b981', border: '#059669' },
    error: { icon: XCircle, bg: '#ef4444', border: '#dc2626' },
    warning: { icon: AlertCircle, bg: '#f59e0b', border: '#d97706' },
    info: { icon: Info, bg: '#3b82f6', border: '#2563eb' }
  };

  const { icon: Icon, bg, border } = config[toast.type] || config.info;

  return (
    <div style={{
      backgroundColor: 'white',
      border: `2px solid ${border}`,
      borderRadius: '12px',
      padding: '16px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      minWidth: '300px',
      opacity: isExiting ? 0 : 1,
      transform: isExiting ? 'translateX(400px)' : 'translateX(0)',
      transition: 'all 0.3s ease-out'
    }}>
      <Icon size={24} style={{ color: bg, flexShrink: 0 }} />
      <p style={{ flex: 1, margin: 0, color: '#2d3748', fontSize: '14px', fontWeight: '500' }}>
        {toast.message}
      </p>
      <button
        onClick={handleClose}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          color: '#6b7280'
        }}
      >
        <X size={18} />
      </button>
    </div>
  );
}

// ============================================
// AUTO-LOGOUT HOOK
// ============================================

function useAutoLogout(onLogout, timeoutMinutes = 30) {
  const timeoutRef = React.useRef(null);
  
  const resetTimer = React.useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    timeoutRef.current = setTimeout(() => {
      console.log('Auto-logout: Session expired due to inactivity');
      onLogout();
    }, timeoutMinutes * 60 * 1000);
  }, [onLogout, timeoutMinutes]);

  React.useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer]);
}

// ============================================
// MAIN APP
// ============================================

function AppContent() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
  const toast = useToast();

  // Data states
  const [houses, setHouses] = useState([]);
  const [zones, setZones] = useState([]);
  const [payments, setPayments] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [subscription, setSubscription] = useState(null);

  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterZone, setFilterZone] = useState('all');
  const [showAddHouse, setShowAddHouse] = useState(false);
  const [showAddZone, setShowAddZone] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentHouse, setPaymentHouse] = useState(null);

  // Auto-logout
  const handleAutoLogout = async () => {
    await supabase.auth.signOut();
    toast.showWarning('Session expired due to inactivity');
  };
  useAutoLogout(handleAutoLogout, 30);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session.user.id);
      } else {
        setUserProfile(null);
        setSubscription(null);
        setLoading(false);
      }
    });

  return () => subscription.unsubscribe();
  }, []);

  // Check if user needs to change password (first login)
  useEffect(() => {
    if (userProfile && userProfile.password_changed === false) {
      setNeedsPasswordChange(true);
    }
  }, [userProfile]);

  const handlePasswordChanged = async () => {
    setNeedsPasswordChange(false);
    // Reload user profile
    if (user) { await loadUserProfile(user.id); }
  };

  // Force password change on first login (INSIDE AppContent)
  if (needsPasswordChange && userProfile) {
    return (
      <ForcePasswordChangeModal
        userEmail={userProfile.email}
        onPasswordChanged={handlePasswordChanged}
      />
    );
  }

  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*, companies(*)')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      
      setUserProfile(data);
      
      if (data.role !== 'super_admin' && data.company_id) {
        try {
          await checkSubscription(data.company_id);
        } catch (error) {
          console.error('Subscription check failed, continuing anyway:', error);
          setSubscription(null);
        }
        loadCompanyData(data.company_id);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async (companyId) => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading subscription:', error);
        return;
      }

      if (!data) {
        setSubscription(null);
        return;
      }

      const today = new Date();
      const trialEnd = data.trial_end_date ? new Date(data.trial_end_date) : null;
      
      let daysRemaining = 0;
      let isBlocked = false;
      let isExpiringSoon = false;
      
      if (trialEnd) {
        const diffTime = trialEnd - today;
        daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        isBlocked = daysRemaining < -2; // 2 day grace period
        isExpiringSoon = daysRemaining <= 5 && daysRemaining > 0;
      }

      if (data.status === 'expired' || data.status === 'cancelled') {
        isBlocked = true;
      }

      setSubscription({ ...data, daysRemaining, isBlocked, isExpiringSoon });
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const loadCompanyData = async (companyId) => {
    try {
      const { data: zonesData } = await supabase
        .from('zones')
        .select('*')
        .eq('company_id', companyId)
        .order('zone_name');
      
      setZones(zonesData || []);

      const { data: apartmentsData } = await supabase
        .from('apartments')
        .select('*')
        .eq('company_id', companyId)
        .order('apartment_name');
      
      setApartments(apartmentsData || []);

      const { data: housesData } = await supabase
        .from('houses')
        .select('*, zones(zone_name, monthly_rate)')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false});
      
      // Work out each unit's balance:
      // charged its zone rate every month since it was added, minus everything paid
      const thisMonth = new Date().toISOString().substring(0, 7); // YYYY-MM
      const { data: allPayments } = await supabase
        .from('payments')
        .select('house_id, amount, payment_month')
        .eq('company_id', companyId);

      const now = new Date();
      setHouses((housesData || []).map(h => {
        const rate = parseFloat(h.zones?.monthly_rate) || 0;
        const added = new Date(h.created_at || now);
        const monthsBilled = Math.max(1,
          (now.getFullYear() - added.getFullYear()) * 12 + (now.getMonth() - added.getMonth()) + 1
        );
        const housePayments = (allPayments || []).filter(p => p.house_id === h.id);
        const totalPaid = housePayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        const paidThisMonth = housePayments
          .filter(p => (p.payment_month || '').substring(0, 7) === thisMonth)
          .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
        const balance = rate * monthsBilled - totalPaid; // > 0 means owing, < 0 means paid ahead

        return {
          ...h,
          monthly_rate: rate,
          total_paid: totalPaid,
          paid_this_month_amount: paidThisMonth,
          balance,
          current_month_paid: balance <= 0
        };
      }));

      const { data: paymentsData } = await supabase
        .from('payments')
        .select('*, houses(house_number, apartment_name, zones(zone_name))')
        .eq('company_id', companyId)
        .order('payment_date', { ascending: false })
        .limit(50);
      
      setPayments(paymentsData || []);

    } catch (error) {
      console.error('Error loading company data:', error);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };


  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f5f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader className="animate-spin" size={48} style={{ color: '#f97316', margin: '0 auto 16px' }} />
          <p style={{ color: '#2d3748' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // This shouldn't happen, but redirect to login
    supabase.auth.signOut();
    return null;
  }

  // Check subscription blocking for non-admin users
  if (userProfile?.role !== 'super_admin' && subscription?.isBlocked) {
    return <SubscriptionBlockedScreen subscription={subscription} onSignOut={handleSignOut} />;
  }

  if (userProfile?.role !== 'super_admin' && userProfile?.company_id && userProfile?.companies?.approval_status !== 'approved') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f8f5f0',
        padding: '20px'
      }}>
        <div style={{ 
          textAlign: 'center', 
          maxWidth: '500px',
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <Clock size={64} style={{ color: '#f97316', margin: '0 auto 24px' }} />
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '16px' }}>
            Account Pending Approval
          </h2>
          <p style={{ color: '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
            Your company account is waiting for admin approval.
          </p>
          <button
            onClick={handleSignOut}
            style={{
              padding: '12px 24px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <LogOut size={20} />
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f5f0' }}>
      {/* Subscription Warning Banner */}
      {userProfile?.role !== 'super_admin' && subscription?.isExpiringSoon && (
        <SubscriptionWarningBanner subscription={subscription} />
      )}

      <div style={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Trash2 size={32} style={{ color: '#f97316' }} />
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#2d3748' }}>
            Garbage Billing System
          </h1>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
              {userProfile?.full_name || user.email}
            </p>
            <p style={{ fontSize: '12px', color: '#6b7280' }}>
              {userProfile?.role === 'super_admin' ? 'Super Admin' : 
               userProfile?.companies?.company_name || 'Company Admin'}
            </p>
          </div>
          <button
            onClick={handleSignOut}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <LogOut size={16} />
            Sign Out
          </button>
        </div>
      </div>

      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {userProfile?.role === 'super_admin' ? (
          <SuperAdminDashboard userProfile={userProfile} toast={toast} />
        ) : (
          <CompanyDashboard 
            userProfile={userProfile}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            houses={houses}
            zones={zones}
            payments={payments}
            apartments={apartments}
            subscription={subscription}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterZone={filterZone}
            setFilterZone={setFilterZone}
            showAddHouse={showAddHouse}
            setShowAddHouse={setShowAddHouse}
            showAddZone={showAddZone}
            setShowAddZone={setShowAddZone}
            selectedHouse={selectedHouse}
            setSelectedHouse={setSelectedHouse}
            showPaymentModal={showPaymentModal}
            setShowPaymentModal={setShowPaymentModal}
            paymentHouse={paymentHouse}
            setPaymentHouse={setPaymentHouse}
            loadCompanyData={loadCompanyData}
            toast={toast}
          />
        )}
      </div>
    </div>
  );
}

// Wrap App with ToastProvider

// =====================================================
// DEMO REQUEST MODAL
// =====================================================

function DemoRequestModal({ demoForm, setDemoForm, handleDemoSubmit, submitting, submitted, onClose }) {
  if (submitted) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '48px',
          maxWidth: '500px',
          width: '90%',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#d1fae5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px'
          }}>
            <CheckCircle size={48} style={{ color: '#10b981' }} />
          </div>
          <h3 style={{ fontSize: '28px', fontWeight: '700', color: '#2d3748', marginBottom: '16px' }}>
            Request Submitted!
          </h3>
          <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
            Thank you for your interest! We've received your demo request and will review it shortly. 
            You'll receive an email with your login credentials once approved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}
    onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '550px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '700', color: '#2d3748' }}>
            Request Free Demo
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} style={{ color: '#6b7280' }} />
          </button>
        </div>

        <p style={{ color: '#6b7280', fontSize: '16px', marginBottom: '32px' }}>
          Fill in your details below and we'll get you started with a 30-day free trial.
        </p>

        <form onSubmit={handleDemoSubmit}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#2d3748',
              fontSize: '14px'
            }}>
              Company Name *
            </label>
            <input
              type="text"
              required
              value={demoForm.company_name}
              onChange={(e) => setDemoForm({ ...demoForm, company_name: e.target.value })}
              placeholder="ABC Waste Management"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#2d3748',
              fontSize: '14px'
            }}>
              Contact Person *
            </label>
            <input
              type="text"
              required
              value={demoForm.contact_person}
              onChange={(e) => setDemoForm({ ...demoForm, contact_person: e.target.value })}
              placeholder="John Doe"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#2d3748',
              fontSize: '14px'
            }}>
              Email Address *
            </label>
            <input
              type="email"
              required
              value={demoForm.contact_email}
              onChange={(e) => setDemoForm({ ...demoForm, contact_email: e.target.value })}
              placeholder="john@company.com"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '32px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#2d3748',
              fontSize: '14px'
            }}>
              Phone Number *
            </label>
            <input
              type="tel"
              required
              value={demoForm.contact_phone}
              onChange={(e) => setDemoForm({ ...demoForm, contact_phone: e.target.value })}
              placeholder="+254 712 345 678"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#2d3748',
              fontSize: '14px'
            }}>
              Password *
            </label>
            <input
              type="password"
              required
              minLength="8"
              value={demoForm.password}
              onChange={(e) => setDemoForm({...demoForm, password: e.target.value})}
              placeholder="Create a password (min 8 characters)"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#2d3748',
              fontSize: '14px'
            }}>
              Confirm Password *
            </label>
            <input
              type="password"
              required
              minLength="8"
              value={demoForm.confirm_password}
              onChange={(e) => setDemoForm({...demoForm, confirm_password: e.target.value})}
              placeholder="Confirm your password"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#6b7280',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 1,
                padding: '14px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: submitting ? '#9ca3af' : '#f97316',
                border: 'none',
                borderRadius: '8px',
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>

        <p style={{ 
          color: '#9ca3af', 
          fontSize: '13px', 
          marginTop: '24px',
          textAlign: 'center',
          lineHeight: '1.5'
        }}>
          By submitting this form, you agree to our terms of service and privacy policy.
        </p>
      </div>
    </div>
  );
}



// =====================================================
// FORCE PASSWORD CHANGE MODAL (First Login)
// =====================================================

function ForcePasswordChangeModal({ userEmail, onPasswordChanged }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Update password in Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      // Mark user as having changed password
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({ password_changed: true })
        .eq('email', userEmail);

      if (profileError) throw profileError;

      onPasswordChanged();

    } catch (error) {
      console.error('Error changing password:', error);
      setError('Error updating password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '500px',
        width: '90%'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          backgroundColor: '#fef3c7',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <Shield size={32} style={{ color: '#f59e0b' }} />
        </div>

        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '12px', textAlign: 'center' }}>
          Change Your Password
        </h3>
        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '32px', textAlign: 'center' }}>
          For security reasons, please change your temporary password before continuing.
        </p>

        <form onSubmit={handlePasswordChange}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>
              New Password
            </label>
            <input
              type="password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 8 characters)"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>
              Confirm Password
            </label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          {error && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              backgroundColor: loading ? '#9ca3af' : '#f97316',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Updating Password...' : 'Change Password'}
          </button>
        </form>

        <p style={{ color: '#9ca3af', fontSize: '12px', marginTop: '20px', textAlign: 'center' }}>
          Password must be at least 8 characters long
        </p>
      </div>
    </div>
  );
}



// =====================================================
// LANDING PAGE COMPONENT
// =====================================================

// =====================================================
// LOGIN MODAL (Inside Landing Page)
// =====================================================

function LoginModal({ onClose, onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (loginError) throw loginError;

      // Success! Close modal and let App handle navigation
      onLoginSuccess();
      
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '450px',
          width: '100%'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '28px', fontWeight: '700', color: '#2d3748' }}>
            Sign In
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} style={{ color: '#6b7280' }} />
          </button>
        </div>

        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
          Enter your credentials to access your dashboard
        </p>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#2d3748',
              fontSize: '14px'
            }}>
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              autoFocus
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              color: '#2d3748',
              fontSize: '14px'
            }}>
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              backgroundColor: loading ? '#9ca3af' : '#f97316',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button
            onClick={async () => {
              if (!email) {
                alert('Please enter your email address first');
                return;
              }
              try {
                const { error } = await supabase.auth.resetPasswordForEmail(email, {
                  redirectTo: 'https://garbage.natsautomations.co.ke'
                });
                if (error) throw error;
                alert('Password reset email sent! Check your inbox.');
              } catch (error) {
                alert('Error: ' + error.message);
              }
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#f97316',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Forgot Password?
          </button>
        </div>

        <p style={{ 
          color: '#6b7280', 
          fontSize: '13px', 
          marginTop: '20px',
          textAlign: 'center'
        }}>
          Don't have an account? <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#f97316',
              fontWeight: '600',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >Request Demo</button>
        </p>
      </div>
    </div>
  );
}


function LandingPage() {
  const toast = useToast();
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [demoForm, setDemoForm] = useState({
    company_name: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    password: '',
    confirm_password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (demoForm.password !== demoForm.confirm_password) {
      toast.showError('Passwords do not match!');
      return;
    }
    
    if (demoForm.password.length < 8) {
      toast.showError('Password must be at least 8 characters!');
      return;
    }
    
    setSubmitting(true);

    try {
      // Save demo request to database with user's password
      const { data, error } = await supabase
        .from('companies')
        .insert([{
          company_name: demoForm.company_name,
          contact_person: demoForm.contact_person,
          contact_email: demoForm.contact_email,
          contact_phone: demoForm.contact_phone,
          user_password: demoForm.password,  // Save user's chosen password
          approval_status: 'pending',
          created_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      setSubmitted(true);
      setTimeout(() => {
        setShowDemoModal(false);
        setSubmitted(false);
        setDemoForm({
          company_name: '',
          contact_person: '',
          contact_email: '',
          contact_phone: ''
        });
      }, 3000);

    } catch (error) {
      console.error('Error submitting demo request:', error);
      alert('Error submitting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#ffffff' }}>
      {/* Navigation Bar */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Trash2 size={32} style={{ color: '#f97316' }} />
          <span style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748' }}>
            Nats Automations
          </span>
        </div>
        <button
          onClick={() => setShowLoginModal(true)}
          style={{
            padding: '10px 24px',
            fontSize: '16px',
            fontWeight: '600',
            backgroundColor: '#f97316',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Sign In
        </button>
      </nav>

      {/* Hero Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '80px 40px',
        textAlign: 'center'
      }}>
        <h1 style={{
          fontSize: '56px',
          fontWeight: '800',
          color: '#2d3748',
          marginBottom: '24px',
          lineHeight: '1.2'
        }}>
          Revolutionize Your <br />
          <span style={{ color: '#f97316' }}>Waste Management</span> Billing
        </h1>
        <p style={{
          fontSize: '20px',
          color: '#6b7280',
          marginBottom: '40px',
          maxWidth: '700px',
          margin: '0 auto 40px'
        }}>
          Streamline billing for apartments, estates, and residential complexes. 
          Automated invoicing, payment tracking, and real-time reports.
        </p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowDemoModal(true)}
            style={{
              padding: '16px 40px',
              fontSize: '18px',
              fontWeight: '700',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(249, 115, 22, 0.3)'
            }}
          >
            Request Free Demo
          </button>
          <button
            onClick={() => setShowLoginModal(true)}
            style={{
              padding: '16px 40px',
              fontSize: '18px',
              fontWeight: '700',
              backgroundColor: 'white',
              color: '#f97316',
              border: '2px solid #f97316',
              borderRadius: '12px',
              cursor: 'pointer'
            }}
          >
            Sign In
          </button>
        </div>
      </div>

      {/* Features Section */}
      <div style={{
        backgroundColor: '#f9fafb',
        padding: '80px 40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '42px',
            fontWeight: '700',
            color: '#2d3748',
            textAlign: 'center',
            marginBottom: '60px'
          }}>
            Everything You Need to Manage Waste Billing
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '40px'
          }}>
            {/* Feature 1 */}
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <Building2 size={48} style={{ color: '#f97316', marginBottom: '20px' }} />
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#2d3748', marginBottom: '12px' }}>
                Apartment Management
              </h3>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
                Organize apartments, houses, and tenants in one central system. Easy categorization by zones.
              </p>
            </div>

            {/* Feature 2 */}
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <DollarSign size={48} style={{ color: '#f97316', marginBottom: '20px' }} />
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#2d3748', marginBottom: '12px' }}>
                Payment Tracking
              </h3>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
                Track payments in real-time. Automatic receipt generation and arrears management.
              </p>
            </div>

            {/* Feature 3 */}
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <FileText size={48} style={{ color: '#f97316', marginBottom: '20px' }} />
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#2d3748', marginBottom: '12px' }}>
                Automated Billing
              </h3>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
                Set monthly rates per zone. Generate bills automatically for all tenants.
              </p>
            </div>

            {/* Feature 4 */}
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <BarChart3 size={48} style={{ color: '#f97316', marginBottom: '20px' }} />
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#2d3748', marginBottom: '12px' }}>
                Real-Time Reports
              </h3>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
                View dashboard analytics. Track collection rates, revenue, and arrears instantly.
              </p>
            </div>

            {/* Feature 5 */}
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <Users size={48} style={{ color: '#f97316', marginBottom: '20px' }} />
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#2d3748', marginBottom: '12px' }}>
                Multi-User Access
              </h3>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
                Role-based access for your team. Secure login and activity tracking.
              </p>
            </div>

            {/* Feature 6 */}
            <div style={{
              backgroundColor: 'white',
              padding: '40px',
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <Shield size={48} style={{ color: '#f97316', marginBottom: '20px' }} />
              <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#2d3748', marginBottom: '12px' }}>
                Secure & Reliable
              </h3>
              <p style={{ color: '#6b7280', fontSize: '16px', lineHeight: '1.6' }}>
                Bank-level security. Cloud-based system with automatic backups.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{
        padding: '80px 40px',
        textAlign: 'center',
        backgroundColor: '#2d3748'
      }}>
        <h2 style={{
          fontSize: '42px',
          fontWeight: '700',
          color: 'white',
          marginBottom: '24px'
        }}>
          Ready to Get Started?
        </h2>
        <p style={{
          fontSize: '20px',
          color: '#9ca3af',
          marginBottom: '40px'
        }}>
          Start your 30-day free trial today. No credit card required.
        </p>
        <button
          onClick={() => setShowDemoModal(true)}
          style={{
            padding: '18px 48px',
            fontSize: '20px',
            fontWeight: '700',
            backgroundColor: '#f97316',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            boxShadow: '0 4px 6px rgba(249, 115, 22, 0.3)'
          }}
        >
          Request Free Demo
        </button>
      </div>

      {/* Footer */}
      <div style={{
        backgroundColor: '#1f2937',
        color: 'white',
        padding: '48px 40px 32px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '40px'
        }}>
          {/* Company Info */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <Trash2 size={28} style={{ color: '#f97316' }} />
              <span style={{ fontSize: '20px', fontWeight: '700', color: 'white' }}>
                Nats Automations
              </span>
            </div>
            <p style={{ color: '#9ca3af', fontSize: '14px', lineHeight: '1.6' }}>
              Modern waste management billing solution for apartments, estates, and residential complexes in Kenya.
            </p>
          </div>

          {/* Contact Info */}
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: 'white' }}>
              Contact Us
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Mail size={18} style={{ color: '#f97316' }} />
                <a 
                  href="mailto:info@natsautomations.co.ke"
                  style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '14px' }}
                >
                  info@natsautomations.co.ke
                </a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Phone size={18} style={{ color: '#f97316' }} />
                <a 
                  href="tel:+254726259977"
                  style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '14px' }}
                >
                  +254 726 259 977
                </a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MapPin size={18} style={{ color: '#f97316' }} />
                <span style={{ color: '#9ca3af', fontSize: '14px' }}>
                  Nairobi, Kenya
                </span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: 'white' }}>
              Quick Links
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <a href="#features" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '14px' }}>
                Features
              </a>
              <a href="#demo" onClick={() => setShowDemoModal(true)} style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '14px', cursor: 'pointer' }}>
                Request Demo
              </a>
              <a href="#signin" onClick={() => setShowLoginModal(true)} style={{ color: '#9ca3af', textDecoration: 'none', fontSize: '14px', cursor: 'pointer' }}>
                Sign In
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div style={{
          maxWidth: '1200px',
          margin: '40px auto 0',
          paddingTop: '24px',
          borderTop: '1px solid #374151',
          textAlign: 'center'
        }}>
          <p style={{ color: '#9ca3af', fontSize: '14px' }}>
            © {new Date().getFullYear()} Nats Automations. All rights reserved.
          </p>
        </div>
      </div>

      {/* Login Modal */}
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={() => {
            setShowLoginModal(false);
            // Login success - App will handle showing dashboard
          }}
        />
      )}

      {/* Demo Request Modal */}
      {showDemoModal && (
        <DemoRequestModal
          demoForm={demoForm}
          setDemoForm={setDemoForm}
          handleDemoSubmit={handleDemoSubmit}
          submitting={submitting}
          submitted={submitted}
          onClose={() => setShowDemoModal(false)}
        />
      )}
    </div>
  );
}



// =====================================================
// AUTO-LOGOUT HOOK (Inactivity Timer)
// =====================================================


function App() {
  
  const [session, setSession] = useState(null);

  // Get session on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Show landing page if not authenticated
  if (!session) {
    return (
      <ToastProvider>
        <LandingPage />
      </ToastProvider>
    );
  }

  // User is logged in - show dashboard
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}

// Continue in next part due to length...

// ============================================
// SUBSCRIPTION COMPONENTS
// ============================================

function SubscriptionWarningBanner({ subscription }) {
  const daysText = subscription.daysRemaining === 1 ? 'day' : 'days';
  const inGrace = subscription.daysRemaining < 0;
  
  return (
    <div style={{
      backgroundColor: inGrace ? '#fee2e2' : '#fef3c7',
      borderBottom: inGrace ? '2px solid #dc2626' : '2px solid #d97706',
      padding: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flexWrap: 'wrap',
      justifyContent: 'center'
    }}>
      <AlertCircle size={20} style={{ color: inGrace ? '#dc2626' : '#d97706' }} />
      <p style={{ 
        margin: 0, 
        color: inGrace ? '#991b1b' : '#92400e',
        fontSize: '14px',
        fontWeight: '600'
      }}>
        {inGrace ? (
          <>Subscription expired {Math.abs(subscription.daysRemaining)} {daysText} ago - Grace period active</>
        ) : (
          <>Trial expires in {subscription.daysRemaining} {daysText}</>
        )}
        {' '} | Monthly Fee: KES {subscription.monthly_fee?.toLocaleString() || '2,000'} | Contact admin to renew
      </p>
    </div>
  );
}

function SubscriptionBlockedScreen({ subscription, onSignOut }) {
  const daysExpired = Math.abs(subscription.daysRemaining);
  
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f8f5f0',
      padding: '20px'
    }}>
      <div style={{
        textAlign: 'center',
        maxWidth: '600px',
        backgroundColor: 'white',
        padding: '48px',
        borderRadius: '16px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          backgroundColor: '#fee2e2',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <Clock size={40} style={{ color: '#dc2626' }} />
        </div>
        
        <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#2d3748', marginBottom: '16px' }}>
          Subscription Expired
        </h2>
        
        <p style={{ color: '#6b7280', marginBottom: '24px', lineHeight: '1.6', fontSize: '16px' }}>
          Your subscription has expired and the grace period has ended.
        </p>
        
        <div style={{
          backgroundColor: '#f3f4f6',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '32px',
          textAlign: 'left'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Monthly Fee:</span>
            <span style={{ fontWeight: '600', color: '#2d3748' }}>
              KES {subscription.monthly_fee?.toLocaleString() || '2,000'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Status:</span>
            <span style={{ fontWeight: '600', color: '#dc2626' }}>{subscription.status?.toUpperCase()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Expired:</span>
            <span style={{ fontWeight: '600', color: '#2d3748' }}>
              {daysExpired} days ago
            </span>
          </div>
        </div>
        
        <p style={{ color: '#2d3748', marginBottom: '24px', fontSize: '15px' }}>
          Please contact <strong>admin@natsautomations.co.ke</strong> to renew your subscription.
        </p>
        
        <button
          onClick={onSignOut}
          style={{
            padding: '12px 32px',
            backgroundColor: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <LogOut size={20} />
          Sign Out
        </button>
      </div>
    </div>
  );
}



// ============================================
// SUBSCRIPTION DISPLAY FOR COMPANY DASHBOARD
// ============================================

function SubscriptionStatusCard({ subscription }) {
  // Handle missing subscription gracefully
  if (!subscription) {
    console.log('No subscription data available');
    return null;
  }

  const getStatusColor = () => {
    if (subscription.isBlocked) return { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' };
    if (subscription.isExpiringSoon) return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
    if (subscription.status === 'trial') return { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' };
    return { bg: '#d1fae5', border: '#10b981', text: '#065f46' };
  };

  const getStatusText = () => {
    if (subscription.isBlocked) return 'BLOCKED';
    if (subscription.status === 'trial') return 'TRIAL';
    if (subscription.status === 'active') return 'ACTIVE';
    if (subscription.status === 'expired') return 'EXPIRED';
    return subscription.status?.toUpperCase();
  };

  const colors = getStatusColor();
  const daysText = Math.abs(subscription.daysRemaining) === 1 ? 'day' : 'days';

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '24px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: `2px solid ${colors.border}`
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Bell size={24} style={{ color: colors.border }} />
            <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#2d3748', margin: 0 }}>
              Subscription Status
            </h4>
            <span style={{
              padding: '4px 12px',
              backgroundColor: colors.bg,
              color: colors.text,
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '700',
              border: `1px solid ${colors.border}`
            }}>
              {getStatusText()}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Monthly Fee</p>
              <p style={{ fontSize: '20px', fontWeight: '700', color: '#2d3748', margin: 0 }}>
                KES {subscription.monthly_fee?.toLocaleString() || '2,000'}
              </p>
            </div>

            {subscription.trial_end_date && (
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                  {subscription.daysRemaining < 0 ? 'Expired' : 'Expires'}
                </p>
                <p style={{ fontSize: '16px', fontWeight: '700', color: subscription.daysRemaining < 0 ? '#ef4444' : '#2d3748', margin: 0 }}>
                  {subscription.daysRemaining < 0 ? (
                    `${Math.abs(subscription.daysRemaining)} ${daysText} ago`
                  ) : (
                    `in ${subscription.daysRemaining} ${daysText}`
                  )}
                </p>
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                  {new Date(subscription.trial_end_date).toLocaleDateString()}
                </p>
              </div>
            )}

            <div>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>Status</p>
              <p style={{ fontSize: '16px', fontWeight: '600', color: colors.text, margin: 0 }}>
                {subscription.status === 'trial' && 'Free trial active'}
                {subscription.status === 'active' && 'Subscription active'}
                {subscription.status === 'expired' && subscription.daysRemaining > -2 && 'Grace period'}
                {subscription.isBlocked && 'Access blocked'}
              </p>
            </div>
          </div>
        </div>

        {subscription.isExpiringSoon && !subscription.isBlocked && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#fef3c7',
            borderRadius: '8px',
            border: '1px solid #f59e0b'
          }}>
            <p style={{ fontSize: '13px', color: '#92400e', fontWeight: '600', margin: 0 }}>
              ⚠️ Renewal needed soon
            </p>
            <p style={{ fontSize: '12px', color: '#92400e', margin: '4px 0 0 0' }}>
              Contact: admin@natsautomations.co.ke
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================
// BASIC APARTMENTS TAB - PHASE 1
// ============================================


// =====================================================
// REDESIGNED APARTMENTS TAB - HOUSES INSIDE
// =====================================================

function ApartmentsTabRedesigned({ 
  apartments, 
  houses, 
  zones,
  onAddApartment, 
  onEditApartment, 
  onDeleteApartment,
  onAddHouse,
  onEditHouse,
  onDeleteHouse,
  onPayHouse,
  companyName,
  toast 
}) {
  const [expandedApartment, setExpandedApartment] = useState(null);
  const [statementHouse, setStatementHouse] = useState(null);
  const [showApartmentModal, setShowApartmentModal] = useState(false);
  const [showHouseModal, setShowHouseModal] = useState(false);
  const [editingApartment, setEditingApartment] = useState(null);
  const [editingHouse, setEditingHouse] = useState(null);
  const [selectedApartmentForHouse, setSelectedApartmentForHouse] = useState(null);

  // Form states
  const [apartmentForm, setApartmentForm] = useState({
    apartment_name: '',
    zone_id: '',
    description: ''
  });

  const [houseForm, setHouseForm] = useState({
    house_number: '',
    zone_id: '',
    tenant_name: '',
    tenant_phone: ''
  });

  // Get houses for a specific apartment
  const getHousesForApartment = (apartmentName) => {
    return houses.filter(house => house.apartment_name === apartmentName);
  };

  // Open modals
  const openAddApartmentModal = () => {
    setEditingApartment(null);
    setApartmentForm({ apartment_name: '', zone_id: zones[0]?.id || '', description: '' });
    setShowApartmentModal(true);
  };

  const openEditApartmentModal = (apartment) => {
    setEditingApartment(apartment);
    setApartmentForm({
      apartment_name: apartment.apartment_name,
      zone_id: apartment.zone_id || zones[0]?.id || '',
      description: apartment.description || ''
    });
    setShowApartmentModal(true);
  };

  const openAddHouseModal = (apartmentName) => {
    setEditingHouse(null);
    setSelectedApartmentForHouse(apartmentName);
    setHouseForm({
      house_number: '',
      zone_id: zones[0]?.id || '',
      tenant_name: '',
      tenant_phone: ''
    });
    setShowHouseModal(true);
  };

  const openEditHouseModal = (house) => {
    setEditingHouse(house);
    setSelectedApartmentForHouse(house.apartment_name);
    setHouseForm({
      house_number: house.house_number,
      zone_id: house.zone_id || '',
      tenant_name: house.tenant_name || '',
      tenant_phone: house.tenant_phone || ''
    });
    setShowHouseModal(true);
  };

  // Handle apartment submit
  const handleApartmentSubmit = async (e) => {
    e.preventDefault();
    
    if (editingApartment) {
      await onEditApartment(editingApartment.id, apartmentForm);
    } else {
      await onAddApartment(apartmentForm);
    }
    
    setShowApartmentModal(false);
  };

  // Handle house submit
  const handleHouseSubmit = async (e) => {
    e.preventDefault();
    
    const houseData = {
      ...houseForm,
      apartment_name: selectedApartmentForHouse
    };

    if (editingHouse) {
      await onEditHouse(editingHouse.id, houseData);
    } else {
      await onAddHouse(houseData);
    }
    
    setShowHouseModal(false);
  };

  return (
    <div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <div>
          <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '4px' }}>
            Apartment Blocks
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            Manage your apartment blocks and their houses
          </p>
        </div>
        <button
          onClick={openAddApartmentModal}
          style={{
            padding: '12px 24px',
            backgroundColor: '#f97316',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={20} />
          New Apartment Block
        </button>
      </div>

      {/* Apartments List */}
      {apartments.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '2px dashed #e5e7eb'
        }}>
          <Building2 size={48} style={{ color: '#9ca3af', margin: '0 auto 16px' }} />
          <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
            No Apartment Blocks Yet
          </h4>
          <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '20px' }}>
            Create your first apartment block to start managing houses
          </p>
          <button
            onClick={openAddApartmentModal}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Create Apartment Block
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {apartments.map(apartment => {
            const apartmentHouses = getHousesForApartment(apartment.apartment_name);
            const isExpanded = expandedApartment === apartment.id;

            return (
              <div
                key={apartment.id}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  border: '2px solid #e5e7eb',
                  overflow: 'hidden'
                }}
              >
                {/* Apartment Header */}
                <div
                  style={{
                    padding: '20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: isExpanded ? '#fef3c7' : 'white'
                  }}
                  onClick={() => setExpandedApartment(isExpanded ? null : apartment.id)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <Building2 size={24} style={{ color: '#f97316' }} />
                      <h4 style={{ fontSize: '20px', fontWeight: '700', color: '#2d3748' }}>
                        {apartment.apartment_name}
                      </h4>
                      <span style={{
                        padding: '4px 12px',
                        backgroundColor: '#dbeafe',
                        color: '#1e40af',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        {apartmentHouses.length} {apartmentHouses.length === 1 ? 'House' : 'Houses'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '24px', color: '#6b7280', fontSize: '14px' }}>
                      <span>
                        <strong>Monthly Rate:</strong> KES {apartment.monthly_rate?.toLocaleString() || '0'}
                      </span>
                      <span>
                        <strong>Occupied:</strong> {apartmentHouses.filter(h => h.tenant_name).length} / {apartmentHouses.length}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditApartmentModal(apartment);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (apartmentHouses.length > 0) {
                          toast.showError('Cannot delete apartment with houses. Delete houses first.');
                        } else if (window.confirm(`Delete ${apartment.apartment_name}?`)) {
                          onDeleteApartment(apartment.id);
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                    {isExpanded ? (
                      <ChevronUp size={24} style={{ color: '#6b7280' }} />
                    ) : (
                      <ChevronDown size={24} style={{ color: '#6b7280' }} />
                    )}
                  </div>
                </div>

                {/* Houses List (Expanded) */}
                {isExpanded && (
                  <div style={{ 
                    padding: '20px',
                    backgroundColor: '#f9fafb',
                    borderTop: '2px solid #e5e7eb'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>
                        Houses in {apartment.apartment_name}
                      </h5>
                      <button
                        onClick={() => openAddHouseModal(apartment.apartment_name)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Plus size={16} />
                        Add House
                      </button>
                    </div>

                    {apartmentHouses.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px dashed #d1d5db'
                      }}>
                        <Home size={32} style={{ color: '#9ca3af', margin: '0 auto 12px' }} />
                        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px' }}>
                          No houses in this apartment yet
                        </p>
                        <button
                          onClick={() => openAddHouseModal(apartment.apartment_name)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Add First House
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {apartmentHouses.map(house => {
                          const zone = zones.find(z => z.id === house.zone_id);
                          
                          return (
                            <div
                              key={house.id}
                              style={{
                                backgroundColor: 'white',
                                padding: '16px',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              <div style={{ display: 'flex', gap: '20px', flex: 1 }}>
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <Home size={18} style={{ color: '#f97316' }} />
                                    <span style={{ fontWeight: '700', fontSize: '16px', color: '#2d3748' }}>
                                      {house.house_number}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                    Zone: {zone?.zone_name || 'Not assigned'}
                                  </div>
                                </div>

                                <div style={{ flex: 1 }}>
                                  {house.tenant_name ? (
                                    <div>
                                      <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '6px',
                                        marginBottom: '4px'
                                      }}>
                                        <User size={16} style={{ color: '#10b981' }} />
                                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>
                                          {house.tenant_name}
                                        </span>
                                        <span style={{
                                          padding: '2px 8px',
                                          backgroundColor: '#d1fae5',
                                          color: '#065f46',
                                          borderRadius: '8px',
                                          fontSize: '11px',
                                          fontWeight: '600'
                                        }}>
                                          OCCUPIED
                                        </span>
                                      </div>
                                      {house.tenant_phone && (
                                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                          📱 {house.tenant_phone}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{
                                        padding: '4px 12px',
                                        backgroundColor: '#fee2e2',
                                        color: '#991b1b',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontWeight: '600'
                                      }}>
                                        VACANT
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                {(() => {
                                  const owes = Math.round(house.balance || 0);
                                  const status = owes <= 0
                                    ? { text: owes < 0 ? `✓ Paid up (KES ${Math.abs(owes).toLocaleString()} ahead)` : '✓ Paid up', bg: '#d1fae5', fg: '#065f46' }
                                    : house.paid_this_month_amount > 0
                                      ? { text: `Partly paid · Owes KES ${owes.toLocaleString()}`, bg: '#ffedd5', fg: '#9a3412' }
                                      : { text: `Unpaid · Owes KES ${owes.toLocaleString()}`, bg: '#fee2e2', fg: '#991b1b' };
                                  return (
                                    <span
                                      title={`Monthly: KES ${(house.monthly_rate || 0).toLocaleString()} · Paid this month: KES ${(house.paid_this_month_amount || 0).toLocaleString()}`}
                                      style={{
                                        padding: '4px 10px',
                                        backgroundColor: status.bg,
                                        color: status.fg,
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontWeight: '600'
                                      }}
                                    >
                                      {status.text}
                                    </span>
                                  );
                                })()}
                                <button
                                  onClick={() => onPayHouse && onPayHouse(house)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                  }}
                                >
                                  💰 Pay
                                </button>
                                <button
                                  onClick={() => setStatementHouse(house)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#8b5cf6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                  }}
                                >
                                  📄 Statement
                                </button>
                                <button
                                  onClick={() => openEditHouseModal(house)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Delete house ${house.house_number}?`)) {
                                      onDeleteHouse(house.id);
                                    }
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Apartment Modal */}
      {showApartmentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '24px' }}>
              {editingApartment ? 'Edit Apartment Block' : 'New Apartment Block'}
            </h3>

            <form onSubmit={handleApartmentSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  Apartment Name *
                </label>
                <input
                  type="text"
                  required
                  value={apartmentForm.apartment_name}
                  onChange={(e) => setApartmentForm({ ...apartmentForm, apartment_name: e.target.value })}
                  placeholder="e.g., Block A, Building 1"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  Zone *
                </label>
                <select
                  required
                  value={apartmentForm.zone_id}
                  onChange={(e) => setApartmentForm({ ...apartmentForm, zone_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select Zone</option>
                  {zones.map(zone => (
                    <option key={zone.id} value={zone.id}>
                      {zone.zone_name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  Description (Optional)
                </label>
                <textarea
                  value={apartmentForm.description}
                  onChange={(e) => setApartmentForm({ ...apartmentForm, description: e.target.value })}
                  placeholder="Additional details about this apartment block..."
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowApartmentModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f97316',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editingApartment ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* House Modal */}
      {showHouseModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '8px' }}>
              {editingHouse ? 'Edit House' : 'Add House'}
            </h3>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
              {selectedApartmentForHouse}
            </p>

            <form onSubmit={handleHouseSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  House Number *
                </label>
                <input
                  type="text"
                  required
                  value={houseForm.house_number}
                  onChange={(e) => setHouseForm({ ...houseForm, house_number: e.target.value })}
                  placeholder="e.g., A1, A2, B1"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  Zone *
                </label>
                <select
                  required
                  value={houseForm.zone_id}
                  onChange={(e) => setHouseForm({ ...houseForm, zone_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                >
                  <option value="">Select zone</option>
                  {zones.map(zone => (
                    <option key={zone.id} value={zone.id}>
                      {zone.zone_name} - KES {zone.monthly_rate?.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  Tenant Name (Optional)
                </label>
                <input
                  type="text"
                  value={houseForm.tenant_name}
                  onChange={(e) => setHouseForm({ ...houseForm, tenant_name: e.target.value })}
                  placeholder="Leave empty if vacant"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  Tenant Phone (Optional)
                </label>
                <input
                  type="tel"
                  value={houseForm.tenant_phone}
                  onChange={(e) => setHouseForm({ ...houseForm, tenant_phone: e.target.value })}
                  placeholder="+254 712 345 678"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowHouseModal(false)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editingHouse ? 'Update' : 'Add House'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {statementHouse && (
        <StatementModal
          house={statementHouse}
          companyName={companyName}
          onClose={() => setStatementHouse(null)}
        />
      )}
    </div>
  );
}

function CreateApartmentModal({ zones, companyId, onClose, onSuccess, toast }) {
  const [apartmentName, setApartmentName] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!apartmentName || !selectedZone) {
      toast.showError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('apartments')
        .insert([{
          company_id: companyId,
          apartment_name: apartmentName,
          zone_id: selectedZone,
          description: description || null,
          total_houses: 0
        }]);

      if (error) throw error;

      toast.showSuccess('Apartment created successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error creating apartment:', error);
      toast.showError('Error creating apartment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%'
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '24px' }}>
          Create Apartment
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Apartment Name *
            </label>
            <input
              type="text"
              required
              value={apartmentName}
              onChange={(e) => setApartmentName(e.target.value)}
              placeholder="e.g., Green Apartments, Sunset Heights"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Zone *
            </label>
            <select
              required
              value={selectedZone}
              onChange={(e) => setSelectedZone(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select zone</option>
              {zones.map(zone => (
                <option key={zone.id} value={zone.id}>
                  {zone.zone_name} - KES {zone.monthly_rate.toLocaleString()}/month
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Additional notes about this apartment..."
              rows={3}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: '#f3f4f6',
                color: '#2d3748',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: loading ? '#cbd5e0' : '#f97316',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Creating...' : 'Create Apartment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// EDIT APARTMENT MODAL
// ============================================

function EditApartmentModal({ apartment, zones, onClose, onSuccess, toast }) {
  const [apartmentName, setApartmentName] = useState(apartment.apartment_name);
  const [selectedZone, setSelectedZone] = useState(apartment.zone_id);
  const [description, setDescription] = useState(apartment.description || '');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!apartmentName || !selectedZone) {
      toast.showError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('apartments')
        .update({
          apartment_name: apartmentName,
          zone_id: selectedZone,
          description: description || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', apartment.id);

      if (error) throw error;

      toast.showSuccess('Apartment updated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error updating apartment:', error);
      toast.showError('Error updating apartment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%'
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '24px' }}>
          Edit Apartment
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Apartment Name *
            </label>
            <input
              type="text"
              required
              value={apartmentName}
              onChange={(e) => setApartmentName(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Zone *
            </label>
            <select
              required
              value={selectedZone}
              onChange={(e) => setSelectedZone(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              {zones.map(zone => (
                <option key={zone.id} value={zone.id}>
                  {zone.zone_name} - KES {zone.monthly_rate.toLocaleString()}/month
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: '#f3f4f6',
                color: '#2d3748',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: loading ? '#cbd5e0' : '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Updating...' : 'Update Apartment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function SuperAdminDashboard({ userProfile, toast }) {
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      
      // Load pending companies
      const { data: pending } = await supabase
        .from('companies')
        .select('*, user_profiles(*)')
        .eq('approval_status', 'pending')
        .order('created_at', { ascending: false });

      // Load all companies
      const { data: companies } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });

      // Load ALL subscriptions separately
      const { data: subscriptions } = await supabase
        .from('subscriptions')
        .select('*');

      console.log('🔍 DEBUG: Loaded companies:', companies?.length);
      console.log('🔍 DEBUG: Loaded subscriptions:', subscriptions?.length);

      // Manually attach subscriptions to companies
      const companiesWithSubs = companies?.map(company => {
        const companySubs = subscriptions?.filter(sub => sub.company_id === company.id) || [];
        console.log(`Company ${company.company_name}: ${companySubs.length} subscriptions`);
        return {
          ...company,
          subscriptions: companySubs
        };
      }) || [];

      setPendingCompanies(pending || []);
      setAllCompanies(companiesWithSubs);
      
      console.log('✅ Final companies with subscriptions:', companiesWithSubs);
    } catch (error) {
      console.error('❌ Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [newFee, setNewFee] = useState('');
  const [newDays, setNewDays] = useState('');

  const openEditFeeModal = (subscription, companyName) => {
    setEditingSubscription({ ...subscription, companyName });
    setNewFee(subscription.monthly_fee?.toString() || '2000');
    setShowEditModal(true);
  };

  const openEditDaysModal = (subscription, companyName) => {
    setEditingSubscription({ ...subscription, companyName });
    setNewDays('30');
    setShowEditModal(true);
  };

  const updateSubscriptionFee = async () => {
    const feeAmount = parseInt(newFee);
    if (isNaN(feeAmount) || feeAmount < 0) {
      toast.showError('Please enter a valid amount');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .update({ monthly_fee: feeAmount })
        .eq('id', editingSubscription.id)
        .select();
      
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Nothing was saved. Check that your SuperAdmin account has permission.');
      }
      
      toast.showSuccess(`Subscription fee updated to KES ${feeAmount.toLocaleString()}!`);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error updating fee:', error);
      toast.showError('Error updating fee: ' + error.message);
    }
  };

  const updateSubscriptionDays = async () => {
    const daysToAdd = parseInt(newDays);
    if (isNaN(daysToAdd) || daysToAdd < 1) {
      toast.showError('Please enter valid number of days');
      return;
    }
    
    try {
      const currentEnd = new Date(editingSubscription.trial_end_date);
      const today = new Date();
      // If already expired, count the extension from today, not the old end date
      const newEnd = currentEnd > today ? new Date(currentEnd) : new Date(today);
      newEnd.setDate(newEnd.getDate() + daysToAdd);
      
      const { data, error } = await supabase
        .from('subscriptions')
        .update({ 
          trial_end_date: newEnd.toISOString().split('T')[0],
          status: 'active'
        })
        .eq('id', editingSubscription.id)
        .select();
      
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Nothing was saved. Check that your SuperAdmin account has permission.');
      }
      
      toast.showSuccess(`${editingSubscription.companyName} subscription extended by ${daysToAdd} days!`);
      setShowEditModal(false);
    } catch (error) {
      console.error('Error extending subscription:', error);
      toast.showError('Error extending subscription: ' + error.message);
    }
  };




  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

    const approveCompany = async (companyId) => {
    try {
      const company = pendingCompanies.find(c => c.id === companyId);
      if (!company) {
        toast.showError('Company not found');
        return;
      }

      // 1. User already set their password during demo request
      const userPassword = company.user_password;
      
      if (!userPassword) {
        toast.showError('No password found for this user. Please reject and ask them to resubmit.');
        return;
      }

      // 2. Save current admin session BEFORE creating user
      const currentSession = await supabase.auth.getSession();
      const adminSession = currentSession.data.session;
      
      if (!adminSession) {
        toast.showError('Admin session lost. Please login again.');
        return;
      }
      
      console.log('Creating auth account for:', company.contact_email);
      
      // 3. Create Supabase Auth account with user's password
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: company.contact_email,
        password: userPassword,
        options: {
          data: {
            full_name: company.contact_person,
            company_name: company.company_name
          }
        }
      });

      if (authError) {
        console.error('Auth error:', authError);
        toast.showError('Error creating account: ' + authError.message);
        return;
      }

      // CRITICAL: Restore admin session immediately
      console.log('Auth account created, restoring admin session...');
      await supabase.auth.setSession({
        access_token: adminSession.access_token,
        refresh_token: adminSession.refresh_token
      });
      
      console.log('✅ Admin session restored');
      
      // 4. Approve the company
      const { error: companyError } = await supabase
        .from('companies')
        .update({ approval_status: 'approved' })
        .eq('id', companyId);

      if (companyError) {
        console.error('Company update error:', companyError);
        toast.showError('Error approving company');
        return;
      }
      
      // 5. Create user profile with company_id
      console.log('🔍 Creating user profile with:', {
        user_id: authData.user.id,
        email: company.contact_email,
        company_id: companyId,
        company_name: company.company_name
      });
      
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .insert([{
          user_id: authData.user.id,
          email: company.contact_email,
          full_name: company.contact_person,
          company_id: companyId,
          role: 'company_admin',
          password_changed: false
        }])
        .select();

      if (profileError) {
        console.error('❌ Profile creation error:', profileError);
        
        // Try to update existing profile instead
        console.log('Trying to update existing profile...');
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ company_id: companyId })
          .eq('user_id', authData.user.id);
        
        if (updateError) {
          console.error('❌ Profile update error:', updateError);
        } else {
          console.log('✅ Profile updated with company_id');
        }
      } else {
        console.log('✅ Profile created:', profileData);
      }

      // 6. Create subscription
      const { data: existingSub } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();

      if (!existingSub) {
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + 30);

        await supabase.from('subscriptions').insert([{
          company_id: companyId,
          trial_end_date: trialEndDate.toISOString().split('T')[0],
          status: 'trial',
          monthly_fee: 2000,
          grace_period_days: 2
        }]);
      }

      // 4. Show credentials in console (user will create account on first login)
      const loginUrl = window.location.origin;
      const emailContent = `
═══════════════════════════════════════════════════════
SEND THIS TO: ${company.contact_email}
═══════════════════════════════════════════════════════

Welcome to Nats Automations!

Your account for ${company.company_name} has been approved.

LOGIN DETAILS:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Email: ${company.contact_email}
Password: (the one you created during demo request)
Login URL: ${loginUrl}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

⚠️ IMPORTANT: 
1. Click "Sign Up" on the login page
2. Use your email and the password you created
3. Complete registration

🎉 30-DAY FREE TRIAL STARTS TODAY!

Need help? Contact us:
📧 info@natsautomations.co.ke
📱 +254 726 259 977

Best regards,
The Nats Automations Team
═══════════════════════════════════════════════════════
      `;

      console.log(emailContent);

      // 5. Try sending email
      try {
        const emailResponse = await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            from: { email: 'garbage@natsautomations.co.ke', name: 'Nats Automations' },
            to: { email: company.contact_email, name: company.contact_person },
            subject: 'Welcome to Nats Automations - Account Approved!',
            html: emailContent.replace(/\n/g, '<br>').replace(/━/g, '─'),
            text: emailContent
          })
        });
        
        if (emailResponse.ok) {
          toast.showSuccess(`${company.company_name} approved! Email sent to ${company.contact_email}`);
        } else {
          toast.showWarning(`${company.company_name} approved! Check console for credentials.`);
        }
      } catch (emailError) {
        toast.showWarning(`${company.company_name} approved! Check console for credentials.`);
      }

      // 7. Reload to refresh pending approvals list
      toast.showSuccess(`${company.company_name} approved successfully!`);
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error approving company:', error);
      toast.showError('Error: ' + error.message);
    }
  };

    const rejectCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to reject this company?')) return;
    
    try {
      const { error } = await supabase
        .from('companies')
        .update({ approval_status: 'rejected' })
        .eq('id', companyId);

      if (error) throw error;
      
      toast.showSuccess('Company rejected');
    } catch (error) {
      console.error('Error rejecting company:', error);
      toast.showSuccess('Error rejecting company');
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>
      <Loader className="animate-spin" size={32} style={{ color: '#f97316', margin: '0 auto' }} />
    </div>;
  }

  return (
    <>
    <div>
      <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#2d3748', marginBottom: '24px' }}>
        Admin Dashboard
      </h2>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '32px', borderBottom: '2px solid #e5e7eb' }}>
        <button
          onClick={() => setActiveTab('pending')}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            border: 'none',
            backgroundColor: 'transparent',
            color: activeTab === 'pending' ? '#f97316' : '#6b7280',
            borderBottom: activeTab === 'pending' ? '3px solid #f97316' : 'none',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          Pending Approvals ({pendingCompanies.length})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            border: 'none',
            backgroundColor: 'transparent',
            color: activeTab === 'all' ? '#f97316' : '#6b7280',
            borderBottom: activeTab === 'all' ? '3px solid #f97316' : 'none',
            cursor: 'pointer',
            marginBottom: '-2px'
          }}
        >
          All Companies ({allCompanies.length})
        </button>
      </div>

      {activeTab === 'pending' && (
        <div>
          {pendingCompanies.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              backgroundColor: 'white',
              borderRadius: '12px'
            }}>
              <CheckCircle size={48} style={{ color: '#10b981', margin: '0 auto 16px' }} />
              <p style={{ color: '#6b7280', fontSize: '16px' }}>
                No pending approvals!
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '16px' }}>
              {pendingCompanies.map((company) => (
                <div key={company.id} style={{
                  backgroundColor: 'white',
                  padding: '24px',
                  borderRadius: '12px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  border: '2px solid #f97316'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                      <h4 style={{ fontSize: '20px', fontWeight: '700', color: '#2d3748', marginBottom: '8px' }}>
                        {company.company_name}
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Mail size={16} style={{ color: '#6b7280' }} />
                          <span style={{ color: '#6b7280', fontSize: '14px' }}>{company.contact_email}</span>
                        </div>
                        {company.contact_phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Phone size={16} style={{ color: '#6b7280' }} />
                            <span style={{ color: '#6b7280', fontSize: '14px' }}>{company.contact_phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => approveCompany(company.id)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <CheckCircle size={16} />
                        Approve
                      </button>
                      <button
                        onClick={() => rejectCompany(company.id)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <XCircle size={16} />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'all' && (
        <div>
          <div style={{ display: 'grid', gap: '12px' }}>
            {allCompanies.map((company) => (
              <div key={company.id} style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '6px' }}>
                      {company.company_name}
                    </h4>
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>{company.contact_email}</p>
                  </div>
                  <div style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '600',
                    backgroundColor: 
                      company.approval_status === 'approved' ? '#d1fae5' :
                      company.approval_status === 'rejected' ? '#fee2e2' : '#fef3c7',
                    color:
                      company.approval_status === 'approved' ? '#065f46' :
                      company.approval_status === 'rejected' ? '#991b1b' : '#92400e'
                  }}>
                    {company.approval_status.toUpperCase()}
                  </div>
                </div>

                {/* Subscription Management */}
                {console.log('Company:', company.company_name, 'Subscriptions:', company.subscriptions)}
                {company.subscriptions && company.subscriptions.length > 0 ? (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    border: '2px solid #3b82f6'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>
                        💳 Subscription
                      </span>
                      <span style={{
                        fontSize: '12px',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontWeight: '700',
                        backgroundColor: 
                          company.subscriptions[0].status === 'trial' ? '#dbeafe' :
                          company.subscriptions[0].status === 'active' ? '#d1fae5' :
                          company.subscriptions[0].status === 'expired' ? '#fee2e2' : '#f3f4f6',
                        color:
                          company.subscriptions[0].status === 'trial' ? '#1e40af' :
                          company.subscriptions[0].status === 'active' ? '#065f46' :
                          company.subscriptions[0].status === 'expired' ? '#991b1b' : '#6b7280'
                      }}>
                        {company.subscriptions[0].status?.toUpperCase()}
                      </span>
                    </div>
                    
                    <div style={{ fontSize: '13px', color: '#4b5563', marginBottom: '12px', display: 'grid', gap: '6px' }}>
                      <div>
                        <strong>Monthly Fee:</strong> KES {company.subscriptions[0].monthly_fee?.toLocaleString() || '2,000'}
                      </div>
                      <div>
                        <strong>Expires:</strong> {new Date(company.subscriptions[0].trial_end_date).toLocaleDateString()}
                      </div>
                      <div>
                        <strong>Days Remaining:</strong> {Math.max(0, Math.ceil((new Date(company.subscriptions[0].trial_end_date) - new Date()) / (1000 * 60 * 60 * 24)))} days
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => openEditFeeModal(company.subscriptions[0], company.company_name)}
                        style={{
                          padding: '8px 14px',
                          fontSize: '13px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Edit Fee
                      </button>
                      <button
                        onClick={() => openEditDaysModal(company.subscriptions[0], company.company_name)}
                        style={{
                          padding: '8px 14px',
                          fontSize: '13px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Extend Subscription
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#fff3cd',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#856404',
                    border: '1px solid #ffc107'
                  }}>
                    ⚠️ No subscription data found for this company
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

      {/* Edit Subscription Modal */}
      {showEditModal && editingSubscription && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '8px' }}>
              {newFee ? 'Edit Monthly Fee' : 'Extend Subscription'}
            </h3>
            <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '24px' }}>
              {editingSubscription.companyName}
            </p>

            {newFee !== '' ? (
              /* Edit Fee Mode */
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  Monthly Fee (KES)
                </label>
                <input
                  type="number"
                  value={newFee}
                  onChange={(e) => setNewFee(e.target.value)}
                  placeholder="Enter amount"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    marginBottom: '24px'
                  }}
                  autoFocus
                />
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setNewFee('');
                    }}
                    style={{
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateSubscriptionFee}
                    style={{
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'white',
                      backgroundColor: '#f59e0b',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Update Fee
                  </button>
                </div>
              </div>
            ) : (
              /* Extend Days Mode */
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2d3748' }}>
                  Number of Days to Add
                </label>
                <input
                  type="number"
                  value={newDays}
                  onChange={(e) => setNewDays(e.target.value)}
                  placeholder="Enter days (e.g., 30)"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '16px',
                    marginBottom: '16px'
                  }}
                  autoFocus
                />
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '8px',
                  marginBottom: '24px'
                }}>
                  <button
                    onClick={() => setNewDays('7')}
                    style={{
                      padding: '10px',
                      fontSize: '13px',
                      backgroundColor: '#e5e7eb',
                      border: '2px solid' + (newDays === '7' ? '#10b981' : '#e5e7eb'),
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    7 Days
                  </button>
                  <button
                    onClick={() => setNewDays('30')}
                    style={{
                      padding: '10px',
                      fontSize: '13px',
                      backgroundColor: '#e5e7eb',
                      border: '2px solid' + (newDays === '30' ? '#10b981' : '#e5e7eb'),
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    30 Days
                  </button>
                  <button
                    onClick={() => setNewDays('90')}
                    style={{
                      padding: '10px',
                      fontSize: '13px',
                      backgroundColor: '#e5e7eb',
                      border: '2px solid' + (newDays === '90' ? '#10b981' : '#e5e7eb'),
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    90 Days
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setNewDays('');
                    }}
                    style={{
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateSubscriptionDays}
                    style={{
                      padding: '12px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'white',
                      backgroundColor: '#10b981',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Extend Subscription
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Company Dashboard with Houses Tab
function CompanyDashboard({ toast, 
  userProfile, activeTab, setActiveTab, houses, zones, payments, apartments, subscription,
  searchTerm, setSearchTerm, filterZone, setFilterZone,
  showAddHouse, setShowAddHouse, showAddZone, setShowAddZone,
  selectedHouse, setSelectedHouse, showPaymentModal, setShowPaymentModal,
  paymentHouse, setPaymentHouse, loadCompanyData
}) {
  const stats = {
    totalHouses: houses.length,
    paidThisMonth: houses.filter(h => h.current_month_paid).length,
    totalArrears: houses.filter(h => !h.current_month_paid).length,
    totalRevenue: houses.reduce((sum, h) => sum + (h.zones?.monthly_rate || 0), 0)
  };

  const filteredHouses = houses.filter(house => {
    const matchesSearch = 
      house.house_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (house.apartment_name && house.apartment_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (house.tenant_name && house.tenant_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesZone = filterZone === 'all' || house.zone_id === filterZone;
    
    return matchesSearch && matchesZone;
  });

  return (
    <div>
      <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#2d3748', marginBottom: '8px' }}>
        {userProfile?.companies?.company_name || 'Company Dashboard'}
      </h2>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        Welcome back, {userProfile?.full_name}!
      </p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #e5e7eb', overflowX: 'auto' }}>
        {['dashboard', 'apartments', 'zones', 'payments'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              border: 'none',
              backgroundColor: 'transparent',
              color: activeTab === tab ? '#f97316' : '#6b7280',
              borderBottom: activeTab === tab ? '3px solid #f97316' : 'none',
              cursor: 'pointer',
              marginBottom: '-2px',
              textTransform: 'capitalize',
              whiteSpace: 'nowrap'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && (
        <>
          <SubscriptionStatusCard subscription={subscription} />
          <DashboardTab 
            stats={stats} 
            setActiveTab={setActiveTab}
            setShowAddHouse={setShowAddHouse}
            setShowAddZone={setShowAddZone}
          />
        </>
      )}

{activeTab === 'zones' && (
        <ZonesTab 
          zones={zones}
          setShowAddZone={setShowAddZone}
          companyId={userProfile.company_id}
          loadCompanyData={loadCompanyData}
        />
      )}

      {activeTab === 'payments' && (
        <PaymentsTab payments={payments} houses={houses}
          companyId={userProfile.company_id}
          companyName={userProfile.companies?.company_name}
          toast={toast} />
      )}
      {activeTab === 'apartments' && (
        <ApartmentsTabRedesigned
          companyName={userProfile.companies?.company_name}
          apartments={apartments}
          houses={houses}
          zones={zones}
          onAddApartment={async (formData) => {
            try {
              const { error } = await supabase
                .from('apartments')
                .insert([{
                  company_id: userProfile.company_id,
                  apartment_name: formData.apartment_name,
                  zone_id: formData.zone_id || null,
                  description: formData.description || ''
                }]);
              
              if (error) throw error;
              toast.showSuccess('Apartment created!');
              loadCompanyData(userProfile.company_id);
            } catch (error) {
              console.error('Error creating apartment:', error);
              toast.showError('Error creating apartment');
            }
          }}
          onEditApartment={async (id, formData) => {
            try {
              const { error } = await supabase
                .from('apartments')
                .update({
                  apartment_name: formData.apartment_name,
                  monthly_rate: parseInt(formData.monthly_rate)
                })
                .eq('id', id);
              
              if (error) throw error;
              toast.showSuccess('Apartment updated!');
              loadCompanyData(userProfile.company_id);
            } catch (error) {
              console.error('Error updating apartment:', error);
              toast.showError('Error updating apartment');
            }
          }}
          onDeleteApartment={async (id) => {
            try {
              const { error } = await supabase
                .from('apartments')
                .delete()
                .eq('id', id);
              
              if (error) throw error;
              toast.showSuccess('Apartment deleted!');
              loadCompanyData(userProfile.company_id);
            } catch (error) {
              console.error('Error deleting apartment:', error);
              toast.showError('Error deleting apartment');
            }
          }}
          onAddHouse={async (formData) => {
            try {
              const { error } = await supabase
                .from('houses')
                .insert([{
                  company_id: userProfile.company_id,
                  house_number: formData.house_number,
                  zone_id: formData.zone_id,
                  apartment_name: formData.apartment_name,
                  tenant_name: formData.tenant_name || null,
                  tenant_phone: formData.tenant_phone || null
                }]);
              
              if (error) throw error;
              toast.showSuccess('House added!');
              loadCompanyData(userProfile.company_id);
            } catch (error) {
              console.error('Error adding house:', error);
              toast.showError('Error adding house');
            }
          }}
          onEditHouse={async (id, formData) => {
            try {
              const { error } = await supabase
                .from('houses')
                .update({
                  house_number: formData.house_number,
                  zone_id: formData.zone_id,
                  apartment_name: formData.apartment_name,
                  tenant_name: formData.tenant_name || null,
                  tenant_phone: formData.tenant_phone || null
                })
                .eq('id', id);
              
              if (error) throw error;
              toast.showSuccess('House updated!');
              loadCompanyData(userProfile.company_id);
            } catch (error) {
              console.error('Error updating house:', error);
              toast.showError('Error updating house');
            }
          }}
          onPayHouse={(house) => {
            const zone = zones.find(z => z.id === house.zone_id);
            setPaymentHouse({ ...house, zones: house.zones || zone });
            setShowPaymentModal(true);
          }}
          onDeleteHouse={async (id) => {
            try {
              const { error } = await supabase
                .from('houses')
                .delete()
                .eq('id', id);
              
              if (error) throw error;
              toast.showSuccess('House deleted!');
              loadCompanyData(userProfile.company_id);
            } catch (error) {
              console.error('Error deleting house:', error);
              toast.showError('Error deleting house');
            }
          }}
          toast={toast}
          companyId={userProfile.company_id}
          loadCompanyData={loadCompanyData}
          toast={toast}
        />
      )}

      {showPaymentModal && paymentHouse && (
        <PaymentModal toast={toast}
          house={paymentHouse}
          companyId={userProfile.company_id}
          companyName={userProfile.companies?.company_name}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentHouse(null);
          }}
          onSuccess={() => {
            setShowPaymentModal(false);
            setPaymentHouse(null);
            loadCompanyData(userProfile.company_id);
          }}
        />
      )}

      {showAddHouse && (
        <AddHouseModal toast={toast}
          zones={zones}
          companyId={userProfile.company_id}
          onClose={() => setShowAddHouse(false)}
          onSuccess={() => {
            setShowAddHouse(false);
            loadCompanyData(userProfile.company_id);
          }}
        />
      )}

      {selectedHouse && (
        <EditHouseModal toast={toast}
          house={selectedHouse}
          zones={zones}
          onClose={() => setSelectedHouse(null)}
          onSuccess={() => {
            setSelectedHouse(null);
            loadCompanyData(userProfile.company_id);
          }}
        />
      )}

      {showAddZone && (
        <AddZoneModal toast={toast}
          companyId={userProfile?.company_id}
          onClose={() => setShowAddZone(false)}
          onSuccess={() => {
            setShowAddZone(false);
            loadCompanyData(userProfile.company_id);
          }}
        />
      )}
    </div>
  );
}

// Dashboard Tab
function DashboardTab({ stats, setActiveTab, setShowAddHouse, setShowAddZone }) {
  return (
    <div>
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px', 
        marginBottom: '32px' 
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Home size={32} style={{ color: '#f97316' }} />
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              Total Houses
            </h3>
          </div>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#2d3748' }}>
            {stats.totalHouses}
          </p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <Check size={32} style={{ color: '#10b981' }} />
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              Paid This Month
            </h3>
          </div>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#10b981' }}>
            {stats.paidThisMonth}
          </p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <AlertCircle size={32} style={{ color: '#ef4444' }} />
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              Arrears
            </h3>
          </div>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#ef4444' }}>
            {stats.totalArrears}
          </p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <DollarSign size={32} style={{ color: '#3b82f6' }} />
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              Monthly Revenue
            </h3>
          </div>
          <p style={{ fontSize: '32px', fontWeight: '700', color: '#3b82f6' }}>
            KES {stats.totalRevenue.toLocaleString()}
          </p>
        </div>
      </div>

      <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '16px' }}>
          Quick Actions
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveTab('apartments')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={18} />
            Add Apartment
          </button>
          <button
            onClick={() => { setActiveTab('zones'); setShowAddZone(true); }}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={18} />
            Add Zone
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            style={{
              padding: '12px 24px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <DollarSign size={18} />
            View Payments
          </button>
        </div>
      </div>
    </div>
  );
}

// Houses Tab (COMPLETE with CRUD)
function HousesTab({ toast, 
  houses, zones, searchTerm, setSearchTerm, filterZone, setFilterZone,
  setShowAddHouse, setSelectedHouse, setPaymentHouse, setShowPaymentModal,
  companyId, loadCompanyData
}) {
  
  const deleteHouse = async (houseId) => {
    if (!window.confirm('Are you sure you want to delete this house?')) return;
    
    try {
      const { error } = await supabase
        .from('houses')
        .delete()
        .eq('id', houseId);

      if (error) throw error;
      
      toast.showSuccess('House deleted successfully!');
      loadCompanyData(companyId);
    } catch (error) {
      console.error('Error deleting house:', error);
      toast.showSuccess('Error deleting house');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748' }}>
          Houses ({houses.length})
        </h3>
        <button
          onClick={() => setShowAddHouse(true)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#f97316',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={18} />
          Add House
        </button>
      </div>

      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '12px', 
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
              <input
                type="text"
                placeholder="Search by house, apartment, or tenant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 10px 10px 40px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
          <div style={{ minWidth: '200px' }}>
            <select
              value={filterZone}
              onChange={(e) => setFilterZone(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="all">All Zones</option>
              {zones.map(zone => (
                <option key={zone.id} value={zone.id}>{zone.zone_name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {houses.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          padding: '60px 20px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Home size={48} style={{ color: '#cbd5e0', margin: '0 auto 16px' }} />
          <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
            No Houses Yet
          </h4>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            Start by adding your first house to the system
          </p>
          <button
            onClick={() => setShowAddHouse(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={18} />
            Add Your First House
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {houses.map((house) => (
            <div key={house.id} style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: house.current_month_paid ? '2px solid #10b981' : '2px solid #fee2e2'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
                      {house.house_number}
                    </h4>
                    {house.apartment_name && (
                      <span style={{ 
                        padding: '4px 12px', 
                        backgroundColor: '#f3f4f6', 
                        color: '#6b7280',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {house.apartment_name}
                      </span>
                    )}
                    <span style={{ 
                      padding: '4px 12px', 
                      backgroundColor: house.current_month_paid ? '#d1fae5' : '#fee2e2',
                      color: house.current_month_paid ? '#065f46' : '#991b1b',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {house.current_month_paid ? 'PAID' : 'UNPAID'}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', color: '#6b7280', fontSize: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MapPin size={16} />
                      <span>{house.zones?.zone_name || 'No Zone'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <DollarSign size={16} />
                      <span>KES {house.zones?.monthly_rate?.toLocaleString() || '0'}/month</span>
                    </div>
                    {house.tenant_name && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <User size={16} />
                        <span>{house.tenant_name}</span>
                      </div>
                    )}
                    {house.tenant_phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Phone size={16} />
                        <span>{house.tenant_phone}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      setPaymentHouse(house);
                      setShowPaymentModal(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    💰 Pay
                  </button>
                  <button
                    onClick={() => setSelectedHouse(house)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Edit size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => deleteHouse(house.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <X size={14} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Zones Tab
function ZonesTab({ zones, setShowAddZone, companyId, loadCompanyData }) {
  const toast = useToast();
  const deleteZone = async (zoneId) => {
    if (!window.confirm('Are you sure? This will affect all houses in this zone.')) return;
    
    try {
      const { error } = await supabase
        .from('zones')
        .delete()
        .eq('id', zoneId);

      if (error) throw error;
      
      toast.showSuccess('Zone deleted successfully!');
      loadCompanyData(companyId);
    } catch (error) {
      console.error('Error deleting zone:', error);
      toast.showError('Error deleting zone: ' + error.message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748' }}>
          Zones ({zones.length})
        </h3>
        <button
          onClick={() => setShowAddZone(true)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={18} />
          Add Zone
        </button>
      </div>

      {zones.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          padding: '60px 20px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <MapPin size={48} style={{ color: '#cbd5e0', margin: '0 auto 16px' }} />
          <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
            No Zones Yet
          </h4>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            Create zones to set different pricing for different areas
          </p>
          <button
            onClick={() => setShowAddZone(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Plus size={18} />
            Create Your First Zone
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {zones.map((zone) => (
            <div key={zone.id} style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '20px', fontWeight: '700', color: '#2d3748', marginBottom: '8px' }}>
                  {zone.zone_name}
                </h4>
                <p style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
                  KES {zone.monthly_rate.toLocaleString()}<span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>/month</span>
                </p>
              </div>
              {zone.description && (
                <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
                  {zone.description}
                </p>
              )}
              <button
                onClick={() => deleteZone(zone.id)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <X size={14} />
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Payment Modal Component
// ============================================
// RECEIPTS, STATEMENTS, WHATSAPP & EXCEL REPORTS
// ============================================

const kes = (n) => `KES ${Math.round(parseFloat(n) || 0).toLocaleString()}`;

const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-GB', { month: 'short', year: 'numeric' });
};

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// 0712 345 678 / +254712345678 / 712345678 -> 254712345678
const toWhatsAppNumber = (phone) => {
  if (!phone) return '';
  let d = String(phone).replace(/\D/g, '');
  if (d.startsWith('0')) d = '254' + d.slice(1);
  else if (d.length === 9 && (d.startsWith('7') || d.startsWith('1'))) d = '254' + d;
  return d;
};

const openWhatsApp = (phone, text) => {
  const num = toWhatsAppNumber(phone);
  const url = num
    ? `https://wa.me/${num}?text=${encodeURIComponent(text)}`
    : `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank');
};

// Opens a printable page; the browser's print dialog can "Save as PDF"
const printDocument = (title, bodyHtml) => {
  const w = window.open('', '_blank');
  if (!w) {
    alert('Please allow pop-ups for this site to print or save as PDF.');
    return;
  }
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title>
    <style>
      body { font-family: Arial, sans-serif; color: #1f2937; max-width: 720px; margin: 24px auto; padding: 0 16px; }
      h1 { font-size: 20px; margin: 0; } h2 { font-size: 16px; margin: 4px 0 16px; color: #6b7280; font-weight: normal; }
      table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 13px; }
      th, td { border-bottom: 1px solid #e5e7eb; padding: 6px 8px; text-align: left; }
      th { background: #f9fafb; } td.n, th.n { text-align: right; }
      .box { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 16px; margin: 12px 0; }
      .owe { color: #dc2626; font-weight: bold; } .ok { color: #059669; font-weight: bold; }
      .muted { color: #6b7280; font-size: 12px; }
    </style></head><body>${bodyHtml}
    <p class="muted">Generated ${new Date().toLocaleString('en-GB')}</p>
    <script>window.onload = () => window.print();</script></body></html>`);
  w.document.close();
};

const generateReceiptNumber = () => {
  const d = new Date();
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  return `RCP-${ymd}-${Math.floor(1000 + Math.random() * 9000)}`;
};

// Month-by-month statement: charged zone rate each month since the unit was added
const buildStatement = (house, payments) => {
  const rate = parseFloat(house.monthly_rate ?? house.zones?.monthly_rate) || 0;
  const now = new Date();
  const start = new Date(house.created_at || now);
  const months = [];
  let y = start.getFullYear(), m = start.getMonth();
  while (y < now.getFullYear() || (y === now.getFullYear() && m <= now.getMonth())) {
    months.push(`${y}-${String(m + 1).padStart(2, '0')}`);
    m++; if (m > 11) { m = 0; y++; }
  }
  if (months.length === 0) months.push(now.toISOString().substring(0, 7));

  let running = 0;
  const rows = months.map(ym => {
    const paid = payments
      .filter(p => (p.payment_month || p.payment_date || '').substring(0, 7) === ym)
      .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    running += rate - paid;
    return { month: ym, charged: rate, paid, balance: running };
  });
  return { rate, rows, balance: running };
};

const unitName = (house) =>
  `${house.apartment_name ? house.apartment_name + ', ' : ''}House ${house.house_number || house.house_name || ''}`;

const receiptText = (companyName, house, payment, balanceAfter) => [
  `*${companyName || 'Garbage Collection'} - Payment Receipt*`,
  `Receipt: ${payment.receipt_number}`,
  `Date: ${new Date(payment.payment_date).toLocaleDateString('en-GB')}`,
  `Unit: ${unitName(house)}`,
  house.tenant_name ? `Tenant: ${house.tenant_name}` : null,
  `Amount paid: ${kes(payment.amount)} (${payment.payment_method || 'cash'})`,
  `For month: ${monthLabel((payment.payment_month || payment.payment_date).substring(0, 7))}`,
  balanceAfter > 0 ? `Balance owing: ${kes(balanceAfter)}` : 'Balance: fully paid. Thank you!',
].filter(Boolean).join('\n');

const receiptHtml = (companyName, house, payment, balanceAfter) => `
  <h1>${escapeHtml(companyName || 'Garbage Collection')}</h1>
  <h2>Payment Receipt</h2>
  <div class="box"><table>
    <tr><th>Receipt No.</th><td>${escapeHtml(payment.receipt_number)}</td></tr>
    <tr><th>Date</th><td>${escapeHtml(new Date(payment.payment_date).toLocaleDateString('en-GB'))}</td></tr>
    <tr><th>Unit</th><td>${escapeHtml(unitName(house))}</td></tr>
    <tr><th>Tenant</th><td>${escapeHtml(house.tenant_name || '-')}</td></tr>
    <tr><th>For month</th><td>${escapeHtml(monthLabel((payment.payment_month || payment.payment_date).substring(0, 7)))}</td></tr>
    <tr><th>Method</th><td>${escapeHtml(payment.payment_method || 'cash')}</td></tr>
    <tr><th>Amount paid</th><td><b>${kes(payment.amount)}</b></td></tr>
    <tr><th>Balance</th><td class="${balanceAfter > 0 ? 'owe' : 'ok'}">${balanceAfter > 0 ? kes(balanceAfter) + ' owing' : 'Fully paid'}</td></tr>
  </table></div>`;

const statementText = (companyName, house, statement, payments) => {
  const recent = statement.rows.slice(-6);
  return [
    `*${companyName || 'Garbage Collection'} - Statement*`,
    `Unit: ${unitName(house)}`,
    house.tenant_name ? `Tenant: ${house.tenant_name}` : null,
    `Monthly charge: ${kes(statement.rate)}`,
    '',
    ...recent.map(r => `${monthLabel(r.month)}: paid ${kes(r.paid)} of ${kes(r.charged)}`),
    '',
    `Payments on record: ${payments.length}`,
    ...payments.slice(0, 5).map(p =>
      `- ${new Date(p.payment_date).toLocaleDateString('en-GB')}: ${kes(p.amount)} (Rcpt ${p.receipt_number || '-'})`),
    '',
    statement.balance > 0 ? `*Total owing: ${kes(statement.balance)}*` : '*Account fully paid. Thank you!*',
  ].filter(v => v !== null).join('\n');
};

const statementHtml = (companyName, house, statement, payments) => `
  <h1>${escapeHtml(companyName || 'Garbage Collection')}</h1>
  <h2>Tenant Statement · ${escapeHtml(unitName(house))}${house.tenant_name ? ' · ' + escapeHtml(house.tenant_name) : ''}</h2>
  <p>Monthly charge: <b>${kes(statement.rate)}</b> &nbsp;·&nbsp; Balance:
    <span class="${statement.balance > 0 ? 'owe' : 'ok'}">${statement.balance > 0 ? kes(statement.balance) + ' owing' : 'Fully paid'}</span></p>
  <table>
    <tr><th>Month</th><th class="n">Charged</th><th class="n">Paid</th><th class="n">Running balance</th></tr>
    ${statement.rows.map(r => `<tr><td>${escapeHtml(monthLabel(r.month))}</td><td class="n">${kes(r.charged)}</td>
      <td class="n">${kes(r.paid)}</td><td class="n">${kes(r.balance)}</td></tr>`).join('')}
  </table>
  <h2 style="margin-top:20px">Payments received</h2>
  <table>
    <tr><th>Date</th><th>Receipt</th><th>Method</th><th class="n">Amount</th><th>Notes</th></tr>
    ${payments.length ? payments.map(p => `<tr><td>${escapeHtml(new Date(p.payment_date).toLocaleDateString('en-GB'))}</td>
      <td>${escapeHtml(p.receipt_number || '-')}</td><td>${escapeHtml(p.payment_method || '-')}</td>
      <td class="n">${kes(p.amount)}</td><td>${escapeHtml(p.notes || '')}</td></tr>`).join('')
      : '<tr><td colspan="5">No payments recorded</td></tr>'}
  </table>`;

// Excel (.xlsx) export. SheetJS is loaded only when needed; falls back to CSV.
const loadSheetJS = () => new Promise((resolve, reject) => {
  if (window.XLSX) return resolve(window.XLSX);
  const s = document.createElement('script');
  s.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js';
  s.onload = () => (window.XLSX ? resolve(window.XLSX) : reject(new Error('Excel library missing')));
  s.onerror = () => reject(new Error('Could not load Excel library'));
  document.head.appendChild(s);
});

const downloadCSV = (filename, rows) => {
  const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

const exportPaymentsReport = async (companyId, companyName, houses) => {
  const { data: payments, error } = await supabase
    .from('payments')
    .select('*')
    .eq('company_id', companyId)
    .order('payment_date', { ascending: false });
  if (error) throw error;

  const byId = Object.fromEntries((houses || []).map(h => [h.id, h]));
  const stamp = new Date().toISOString().substring(0, 10);
  const safeName = (companyName || 'Company').replace(/[^\w\- ]+/g, '').trim() || 'Company';

  const balanceRows = [
    ['Apartment', 'Unit', 'Tenant', 'Phone', 'Zone', 'Monthly charge (KES)', 'Paid this month (KES)', 'Total paid (KES)', 'Balance owing (KES)', 'Status'],
    ...[...(houses || [])]
      .sort((a, b) => `${a.apartment_name}${a.house_number}`.localeCompare(`${b.apartment_name}${b.house_number}`, undefined, { numeric: true }))
      .map(h => [
        h.apartment_name || '', h.house_number || h.house_name || '', h.tenant_name || '', h.tenant_phone || '',
        h.zones?.zone_name || '', Math.round(h.monthly_rate || 0), Math.round(h.paid_this_month_amount || 0),
        Math.round(h.total_paid || 0), Math.round(Math.max(h.balance || 0, 0)),
        (h.balance || 0) <= 0 ? 'Paid up' : (h.paid_this_month_amount > 0 ? 'Partly paid' : 'Unpaid'),
      ]),
  ];

  const paymentRows = [
    ['Date', 'Receipt No.', 'Apartment', 'Unit', 'Tenant', 'For month', 'Method', 'Amount (KES)', 'Notes'],
    ...(payments || []).map(p => {
      const h = byId[p.house_id] || {};
      return [
        p.payment_date, p.receipt_number || '', h.apartment_name || '', h.house_number || h.house_name || '',
        h.tenant_name || '', (p.payment_month || '').substring(0, 7), p.payment_method || '',
        parseFloat(p.amount) || 0, p.notes || '',
      ];
    }),
  ];

  try {
    const XLSX = await loadSheetJS();
    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.aoa_to_sheet(balanceRows);
    ws1['!cols'] = [18, 10, 20, 14, 14, 14, 14, 14, 14, 12].map(w => ({ wch: w }));
    const ws2 = XLSX.utils.aoa_to_sheet(paymentRows);
    ws2['!cols'] = [12, 18, 18, 10, 20, 10, 10, 12, 30].map(w => ({ wch: w }));
    XLSX.utils.book_append_sheet(wb, ws1, 'Unit Balances');
    XLSX.utils.book_append_sheet(wb, ws2, 'All Payments');
    XLSX.writeFile(wb, `${safeName} Payments Report ${stamp}.xlsx`);
    return 'xlsx';
  } catch (e) {
    console.warn('Excel export failed, downloading CSV instead:', e);
    downloadCSV(`${safeName} Unit Balances ${stamp}.csv`, balanceRows);
    downloadCSV(`${safeName} All Payments ${stamp}.csv`, paymentRows);
    return 'csv';
  }
};

// Statement window for one unit
function StatementModal({ house, companyName, onClose }) {
  const [payments, setPayments] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('house_id', house.id)
        .order('payment_date', { ascending: false });
      if (error) setError(error.message);
      setPayments(data || []);
    })();
  }, [house.id]);

  const statement = payments ? buildStatement(house, payments) : null;

  const btn = (bg) => ({
    padding: '10px 16px', backgroundColor: bg, color: 'white', border: 'none',
    borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', flex: '1 1 auto'
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px', padding: '28px', maxWidth: '640px',
        width: '100%', maxHeight: '90vh', overflowY: 'auto'
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#2d3748', marginBottom: '4px' }}>Statement</h3>
        <p style={{ color: '#6b7280', marginBottom: '16px' }}>
          {unitName(house)}{house.tenant_name ? ` · ${house.tenant_name}` : ''}
        </p>

        {!payments && <p style={{ color: '#6b7280' }}>Loading payments...</p>}
        {error && <p style={{ color: '#dc2626' }}>Error: {error}</p>}

        {statement && (
          <>
            <div style={{
              padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
              backgroundColor: statement.balance > 0 ? '#fee2e2' : '#d1fae5',
              color: statement.balance > 0 ? '#991b1b' : '#065f46', fontWeight: '700'
            }}>
              {statement.balance > 0 ? `Owes ${kes(statement.balance)}` : 'Fully paid'}
              <span style={{ fontWeight: '400' }}> · Monthly charge {kes(statement.rate)}</span>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>Month</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Charged</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Paid</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {[...statement.rows].reverse().map(r => (
                  <tr key={r.month} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '8px' }}>{monthLabel(r.month)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{kes(r.charged)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', color: r.paid >= r.charged ? '#059669' : r.paid > 0 ? '#c2410c' : '#dc2626' }}>
                      {kes(r.paid)}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600' }}>{kes(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#2d3748', marginBottom: '8px' }}>
              Payments received ({payments.length})
            </h4>
            {payments.length === 0 ? (
              <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '20px' }}>No payments recorded yet.</p>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                {payments.map(p => (
                  <div key={p.id} style={{
                    display: 'flex', justifyContent: 'space-between', gap: '8px', padding: '8px 0',
                    borderBottom: '1px solid #f3f4f6', fontSize: '13px', flexWrap: 'wrap'
                  }}>
                    <span>{new Date(p.payment_date).toLocaleDateString('en-GB')} · Rcpt {p.receipt_number || '-'} · {p.payment_method || 'cash'}</span>
                    <b>{kes(p.amount)}</b>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button style={btn('#25D366')}
                onClick={() => openWhatsApp(house.tenant_phone, statementText(companyName, house, statement, payments))}>
                Send via WhatsApp
              </button>
              <button style={btn('#3b82f6')}
                onClick={() => printDocument(`Statement ${unitName(house)}`, statementHtml(companyName, house, statement, payments))}>
                Print / Save PDF
              </button>
              <button style={btn('#6b7280')} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PaymentModal({ house, companyId, companyName, onClose, onSuccess, toast }) {
  const [formData, setFormData] = useState({
    amount: house.balance > 0 ? Math.round(house.balance) : (house.zones?.monthly_rate || 0),
    receipt_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [savedPayment, setSavedPayment] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const receiptNumber = formData.receipt_number.trim() || generateReceiptNumber();

    setLoading(true);
    try {
      // Insert payment record
      const { data: paymentRow, error: paymentError } = await supabase
        .from('payments')
        .insert({
          house_id: house.id,
          company_id: companyId,
          amount: parseFloat(formData.amount),
          receipt_number: receiptNumber,
          payment_date: formData.payment_date,
          payment_month: formData.payment_date.substring(0, 7), // YYYY-MM format
          payment_method: formData.payment_method,
          notes: formData.notes
        })
        .select()
        .single();

      if (paymentError) throw paymentError;

      // Update house payment status
      const { error: houseError } = await supabase
        .from('houses')
        .update({ current_month_paid: true })
        .eq('id', house.id);

      if (houseError) throw houseError;

      toast.showSuccess('Payment recorded successfully!');
      setSavedPayment(paymentRow);
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.showSuccess('Error recording payment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (savedPayment) {
    const balanceAfter = (house.balance || 0) - (parseFloat(savedPayment.amount) || 0);
    const rbtn = (bg) => ({
      padding: '12px 16px', backgroundColor: bg, color: 'white', border: 'none', borderRadius: '8px',
      fontSize: '14px', fontWeight: '600', cursor: 'pointer', flex: '1 1 auto'
    });
    return (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
      }} onClick={onSuccess}>
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '28px', maxWidth: '460px',
          width: '100%', maxHeight: '90vh', overflowY: 'auto'
        }} onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '36px' }}>✅</div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#2d3748' }}>Payment Receipt</h3>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>{companyName}</p>
          </div>
          <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '16px', fontSize: '14px', marginBottom: '20px' }}>
            {[
              ['Receipt No.', savedPayment.receipt_number],
              ['Date', new Date(savedPayment.payment_date).toLocaleDateString('en-GB')],
              ['Unit', unitName(house)],
              ['Tenant', house.tenant_name || '-'],
              ['Method', savedPayment.payment_method || 'cash'],
              ['Amount paid', kes(savedPayment.amount)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #eef0f3' }}>
                <span style={{ color: '#6b7280' }}>{k}</span><b style={{ color: '#2d3748' }}>{v}</b>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px' }}>
              <span style={{ color: '#6b7280' }}>Balance</span>
              <b style={{ color: balanceAfter > 0 ? '#dc2626' : '#059669' }}>
                {balanceAfter > 0 ? `${kes(balanceAfter)} owing` : 'Fully paid'}
              </b>
            </div>
          </div>
          {!house.tenant_phone && (
            <p style={{ fontSize: '12px', color: '#92400e', marginBottom: '12px' }}>
              No phone number saved for this unit. WhatsApp will ask you to pick the contact.
            </p>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button style={rbtn('#25D366')}
              onClick={() => openWhatsApp(house.tenant_phone, receiptText(companyName, house, savedPayment, balanceAfter))}>
              Send via WhatsApp
            </button>
            <button style={rbtn('#3b82f6')}
              onClick={() => printDocument(`Receipt ${savedPayment.receipt_number}`, receiptHtml(companyName, house, savedPayment, balanceAfter))}>
              Print / Save PDF
            </button>
            <button style={rbtn('#6b7280')} onClick={onSuccess}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '8px' }}>
          Record Payment
        </h3>
        <p style={{ color: '#6b7280', marginBottom: '16px' }}>
          House {house.house_number}{house.apartment_name ? ` - ${house.apartment_name}` : ''}
          {house.tenant_name ? ` · ${house.tenant_name}` : ''}
        </p>

        {house.balance !== undefined && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px',
            marginBottom: '20px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            fontSize: '13px'
          }}>
            <div>
              <div style={{ color: '#6b7280' }}>Monthly charge</div>
              <div style={{ fontWeight: '700', color: '#2d3748' }}>KES {(house.monthly_rate || 0).toLocaleString()}</div>
            </div>
            <div>
              <div style={{ color: '#6b7280' }}>Paid this month</div>
              <div style={{ fontWeight: '700', color: '#2d3748' }}>KES {(house.paid_this_month_amount || 0).toLocaleString()}</div>
            </div>
            <div>
              <div style={{ color: '#6b7280' }}>{house.balance > 0 ? 'Owes (incl. arrears)' : 'Balance'}</div>
              <div style={{ fontWeight: '700', color: house.balance > 0 ? '#dc2626' : '#059669' }}>
                {house.balance > 0
                  ? `KES ${Math.round(house.balance).toLocaleString()}`
                  : house.balance < 0 ? `KES ${Math.abs(Math.round(house.balance)).toLocaleString()} ahead` : 'Paid up'}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Amount (KES) *
            </label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Receipt Number
            </label>
            <input
              type="text"
              value={formData.receipt_number}
              onChange={(e) => setFormData({ ...formData, receipt_number: e.target.value })}
              placeholder="Leave blank to generate one automatically"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Payment Date *
            </label>
            <input
              type="date"
              required
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Payment Method *
            </label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="cash">Cash</option>
              <option value="mpesa">M-Pesa</option>
              <option value="bank">Bank Transfer</option>
              <option value="cheque">Cheque</option>
            </select>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes..."
              rows="3"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: '#f3f4f6',
                color: '#2d3748',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: loading ? '#cbd5e0' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Recording...' : '💰 Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Payments Tab Component
function PaymentsTab({ payments, houses, apartments, companyId, companyName, toast }) {
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const kind = await exportPaymentsReport(companyId, companyName, houses);
      if (kind === 'csv') toast?.showWarning?.('Excel was unavailable, so two CSV files were downloaded instead. They open in Excel.');
      else toast?.showSuccess?.('Excel report downloaded');
    } catch (err) {
      console.error('Export failed:', err);
      toast?.showError?.('Could not create report: ' + err.message);
    } finally {
      setExporting(false);
    }
  };
  const [filterApartment, setFilterApartment] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Get unique apartments
  const apartmentNames = [...new Set(
    houses.filter(h => h.apartment_name).map(h => h.apartment_name)
  )].sort();

  const filteredPayments = payments.filter(payment => {
    const matchesSearch = searchTerm === '' ||
      payment.receipt_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (payment.houses?.house_number && payment.houses.house_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.houses?.tenant_name && payment.houses.tenant_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (payment.notes && payment.notes.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesApartment = filterApartment === 'all' || 
      payment.houses?.apartment_name === filterApartment;
    
    return matchesSearch && matchesApartment;
  });

  const totalCollected = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748' }}>
            Payment History
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '4px' }}>
            Total Collected: KES {totalCollected.toLocaleString()}
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          style={{
            padding: '10px 18px',
            backgroundColor: exporting ? '#9ca3af' : '#16a34a',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: exporting ? 'default' : 'pointer'
          }}
        >
          {exporting ? 'Preparing...' : '📊 Download Excel Report'}
        </button>
      </div>

      {/* Search and Filter */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '12px', 
        marginBottom: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by receipt, house, or notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 10px 10px 40px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
            </div>
         
          <div style={{ minWidth: '200px' }}>
            <select
              value={filterApartment}
              onChange={(e) => setFilterApartment(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="all">All Apartments</option>
              {apartmentNames.map(apt => (
                <option key={apt} value={apt}>{apt}</option>
              ))}
            </select>
          </div> </div>
        </div>
      </div>

      {/* Payments List */}
      {filteredPayments.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          padding: '60px 20px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <DollarSign size={48} style={{ color: '#cbd5e0', margin: '0 auto 16px' }} />
          <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
            No Payments Yet
          </h4>
          <p style={{ color: '#6b7280' }}>
            Payment records will appear here once you start recording payments
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {filteredPayments.map((payment) => (
            <div key={payment.id} style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '12px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: '2px solid #10b981'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <h4 style={{ fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
                      House {payment.houses?.house_number || 'N/A'}
                    </h4>
                    {payment.houses?.apartment_name && (
                      <span style={{ 
                        padding: '4px 12px', 
                        backgroundColor: '#f3f4f6', 
                        color: '#6b7280',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {payment.houses.apartment_name}
                      </span>
                    )}
                    <span style={{ 
                      padding: '4px 12px', 
                      backgroundColor: '#d1fae5',
                      color: '#065f46',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {payment.payment_method || 'Cash'}
                    </span>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px', color: '#6b7280', fontSize: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <DollarSign size={16} />
                      <span style={{ fontWeight: '600', color: '#10b981', fontSize: '16px' }}>
                        KES {payment.amount?.toLocaleString() || '0'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FileText size={16} />
                      <span>Receipt: {payment.receipt_number}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Calendar size={16} />
                      <span>{new Date(payment.payment_date).toLocaleDateString()}</span>
                    </div>
                    {payment.houses?.zones?.zone_name && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <MapPin size={16} />
                        <span>{payment.houses.zones.zone_name}</span>
                      </div>
                    )}
                  </div>
                  
                  {payment.notes && (
                    <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '12px', fontStyle: 'italic' }}>
                      Note: {payment.notes}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Add House Modal (COMPLETE)
function AddHouseModal({ zones, companyId, onClose, onSuccess, toast }) {
  const [formData, setFormData] = useState({
    house_number: '',
    apartment_name: '',
    zone_id: '',
    tenant_name: '',
    tenant_phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.house_number || !formData.zone_id) {
      toast.showSuccess('Please fill in required fields (House Number and Zone)');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('houses')
        .insert({
          ...formData,
          company_id: companyId,
          current_month_paid: false
        });

      if (error) throw error;

      toast.showSuccess('House added successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error adding house:', error);
      toast.showSuccess('Error adding house: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '24px' }}>
          Add New House
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              House Number *
            </label>
            <input
              type="text"
              required
              value={formData.house_number}
              onChange={(e) => setFormData({ ...formData, house_number: e.target.value })}
              placeholder="e.g., 23, 45, A12"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Apartment/Building Name
            </label>
            <input
              type="text"
              value={formData.apartment_name}
              onChange={(e) => setFormData({ ...formData, apartment_name: e.target.value })}
              placeholder="e.g., Green Apartments"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Zone *
            </label>
            <select
              required
              value={formData.zone_id}
              onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select a zone</option>
              {zones.map(zone => (
                <option key={zone.id} value={zone.id}>
                  {zone.zone_name} - KES {zone.monthly_rate.toLocaleString()}/month
                </option>
              ))}
            </select>
            {zones.length === 0 && (
              <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                Please create a zone first
              </p>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Tenant Name
            </label>
            <input
              type="text"
              value={formData.tenant_name}
              onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
              placeholder="e.g., John Doe"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Tenant Phone
            </label>
            <input
              type="tel"
              value={formData.tenant_phone}
              onChange={(e) => setFormData({ ...formData, tenant_phone: e.target.value })}
              placeholder="e.g., 0712345678"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: '#f3f4f6',
                color: '#2d3748',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || zones.length === 0}
              style={{
                padding: '10px 24px',
                backgroundColor: loading || zones.length === 0 ? '#cbd5e0' : '#f97316',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading || zones.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? <Loader className="animate-spin" size={16} /> : <Plus size={16} />}
              {loading ? 'Adding...' : 'Add House'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit House Modal (COMPLETE)
function EditHouseModal({ house, zones, onClose, onSuccess, toast }) {
  const [formData, setFormData] = useState({
    house_number: house.house_number,
    apartment_name: house.apartment_name || '',
    zone_id: house.zone_id,
    tenant_name: house.tenant_name || '',
    tenant_phone: house.tenant_phone || '',
    current_month_paid: house.current_month_paid
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase
        .from('houses')
        .update(formData)
        .eq('id', house.id);

      if (error) throw error;

      toast.showSuccess('House updated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error updating house:', error);
      toast.showSuccess('Error updating house: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '24px' }}>
          Edit House
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              House Number *
            </label>
            <input
              type="text"
              required
              value={formData.house_number}
              onChange={(e) => setFormData({ ...formData, house_number: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Apartment/Building Name
            </label>
            <input
              type="text"
              value={formData.apartment_name}
              onChange={(e) => setFormData({ ...formData, apartment_name: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Zone *
            </label>
            <select
              required
              value={formData.zone_id}
              onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              {zones.map(zone => (
                <option key={zone.id} value={zone.id}>
                  {zone.zone_name} - KES {zone.monthly_rate.toLocaleString()}/month
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Tenant Name
            </label>
            <input
              type="text"
              value={formData.tenant_name}
              onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Tenant Phone
            </label>
            <input
              type="tel"
              value={formData.tenant_phone}
              onChange={(e) => setFormData({ ...formData, tenant_phone: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.current_month_paid}
                onChange={(e) => setFormData({ ...formData, current_month_paid: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                Paid this month
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: '#f3f4f6',
                color: '#2d3748',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: loading ? '#cbd5e0' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? <Loader className="animate-spin" size={16} /> : <Edit size={16} />}
              {loading ? 'Updating...' : 'Update House'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Add Zone Modal
function AddZoneModal({ companyId, onClose, onSuccess, toast }) {
  const [formData, setFormData] = useState({
    zone_name: '',
    monthly_rate: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.zone_name || !formData.monthly_rate) {
      toast.showSuccess('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      console.log('🔍 Creating zone with company_id:', companyId);
      console.log('🔍 Form data:', formData);
      
      if (!companyId) {
        throw new Error('company_id is required but is: ' + companyId);
      }
      
      const { error } = await supabase
        .from('zones')
        .insert({
          ...formData,
          monthly_rate: parseFloat(formData.monthly_rate),
          company_id: companyId
        });

      if (error) throw error;

      toast.showSuccess('Zone added successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error adding zone:', error);
      toast.showSuccess('Error adding zone: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%'
      }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#2d3748', marginBottom: '24px' }}>
          Add New Zone
        </h3>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Zone Name *
            </label>
            <input
              type="text"
              required
              value={formData.zone_name}
              onChange={(e) => setFormData({ ...formData, zone_name: e.target.value })}
              placeholder="e.g., Westlands, CBD, Kileleshwa"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Monthly Rate (KES) *
            </label>
            <input
              type="number"
              required
              min="0"
              step="0.01"
              value={formData.monthly_rate}
              onChange={(e) => setFormData({ ...formData, monthly_rate: e.target.value })}
              placeholder="e.g., 500"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional notes about this zone"
              rows="3"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: '#f3f4f6',
                color: '#2d3748',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                backgroundColor: loading ? '#cbd5e0' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? <Loader className="animate-spin" size={16} /> : <Plus size={16} />}
              {loading ? 'Adding...' : 'Add Zone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}




export default App;
